import Vue, { PropType } from "vue";
import { Puzzle } from "js/crossword/Puzzle";
import { IPopupData, IUIData, PopupType } from "js/crossword/html/types";
import { getDefaultSettings, ISettingsData } from "js/crossword/settingsData";
import { ICalendarDayConfig } from "js/crossword/calendarData";
import { RevealType, PuzzleType } from "js/crossword/types";

import AlreadyRevealedPopup from "./popups/AlreadyRevealedPopup";
import CalendarPopup from "./popups/CalendarPopup";
import ChangeDayAdPopup from "./popups/ChangeDayAdPopup";
import ClearPuzzlePopup from "./popups/ClearPuzzlePopup";
import CompletePopup from "./popups/CompletePopup";
import NotCompletePopup from "./popups/NotCompletePopup";
import OutOfHintsAdPopup from "./popups/OutOfHintsAdPopup";
import PausePopup from "./popups/PausePopup";
import PrintAdPopup from "./popups/PrintAdPopup";
import RevealPuzzleAdPopup from "./popups/RevealPuzzleAdPopup";
import SettingsPopup from "./popups/SettingsPopup";
import WelcomePopup from "./popups/WelcomePopup";

import styles from "./css/Popup.module.css";

export default Vue.extend({
  props: {
    puzzle: Puzzle,
    ui: Object as PropType<IUIData>,
    settings: Object as PropType<ISettingsData>,
  },
  computed: {
    hideLayoutOptions(): boolean {
      return this.puzzle.data.type === PuzzleType.Arrow;
    },
  },
  methods: {
    print(printType) {
      this.$emit("printPuzzle", printType);
    },
    openCalendar() {
      this.ui.popup = { type: PopupType.Calendar, data: null };
    },
    closePopup() {
      this.ui.popup = null;
    },
    handlePickedDay(dayConfig: ICalendarDayConfig) {
      this.ui.popup = { type: PopupType.ChangeDayAd, data: dayConfig };
    },
    setDefaultSettings() {
      Object.assign(this.settings, getDefaultSettings());
    },
    loadPuzzle(url: string) {
      this.$emit("loadPuzzle", url);
    },
    reveal(type: RevealType) {
      if (type === RevealType.Puzzle) {
        this.puzzle.revealPuzzle();
      } else if (type === RevealType.Word) {
        this.puzzle.revealWord();
      } else {
        this.puzzle.revealLetter();
      }
    },
    getPopup(popup: IPopupData) {
      const {
        puzzle,
        settings,
        hideLayoutOptions,
      } = this;

      const puzzleId = puzzle.data.id;
      const puzzleDate = puzzle.data.metaData.date
      const { type, data } = popup;
      const { revealType, withTimer, gameTitle } = data || {};

      const close = () => this.closePopup();
      
      switch (type) {
        case PopupType.AlreadyRevealedPopup: return (
          <AlreadyRevealedPopup onClose={close} type={revealType}/>
        );
        case PopupType.Calendar: return(
          <CalendarPopup 
            onClose={close} 
            onPickedDay={this.handlePickedDay} 
            puzzleId={puzzleId}
            puzzleDate={puzzleDate}
          />
        );
        case PopupType.ChangeDayAd: return (
          <ChangeDayAdPopup
            onClose={close}
            onOpenCalendar={this.openCalendar}
            onLoadPuzzle={this.loadPuzzle}
            dayData={data}
          />
        );
        case PopupType.ClearPuzzle: return (
          <ClearPuzzlePopup onClose={close} puzzle={puzzle} withTimer={withTimer}/>
        );
        case PopupType.Complete: return (
          <CompletePopup onClose={close} onOpenCalendar={this.openCalendar} puzzle={puzzle}/>
        );
        case PopupType.NotComplete: return (
          <NotCompletePopup
            onClose={close}
            onShowMistakes={puzzle.checkPuzzle.bind(puzzle)}
            puzzleId={puzzleId}
          />
        );
        case PopupType.OutOfHintsAd: return (
          <OutOfHintsAdPopup onClose={close} onReveal={this.reveal} type={revealType}/>
        );
        case PopupType.Pause: return (
          <PausePopup onClose={close} puzzleId={puzzleId}/>
        );
        case PopupType.Print: return (
          <PrintAdPopup onClose={close} onPrintPuzzle={this.print}/>
        );
        case PopupType.RevealPuzzleAd: return (
          <RevealPuzzleAdPopup onClose={close} onReveal={this.reveal}/>
        );
        case PopupType.Settings: return (
          <SettingsPopup
            onClose={close}
            onSetDefaultSettings={this.setDefaultSettings}
            hideLayoutOptions={hideLayoutOptions}
            settings={settings}
          />
        );
        case PopupType.Welcome: return (
          <WelcomePopup onClose={close} puzzle={puzzle} gameTitle={gameTitle}/>
        );
        default:
          throw new Error(`Popup of type '${type}' is not configured.`);
      }
    }
  },
  render() {
    const popup = this.ui.popup;
    if (!popup) return <span/>

    return( 
      <div class={styles.popupOverlay}>
        { this.getPopup(popup) }
      </div>
    );
  },
});
